import React, { useState } from 'react';
import { Download, ZoomIn, ZoomOut, RotateCcw, Image as ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';

interface InfographicTabProps {
    lessonTitle?: string;
    lessonContent?: string;
    lessonId?: string;
    lop?: string;
    chuong?: string;
    bai?: string;
    content?: any;
}

const InfographicTab: React.FC<InfographicTabProps> = ({ lessonTitle = '', lop = '', chuong = '', bai = '', content }) => {
    const [imageError, setImageError] = useState(false);
    const [scale, setScale] = useState(1);
    const [ext, setExt] = useState<'webp' | 'png'>('webp'); // ưu tiên WebP (nhẹ), fallback PNG nếu chưa convert

    // Determine path — mặc định WebP đã tối ưu; ảnh từ teacher content thì dùng URL gốc
    const fromContent = !!content?.contentData?.url;
    let imagePath = `/infographic/${lop}/${chuong}/${bai}/infographic.${ext}`;

    // Override if teacher content is provided
    if (content?.contentData?.url) {
        let rawUrl = content.contentData.url;

        // Handle teacher content path resolution similar to video/simulation
        if (rawUrl.startsWith('/teacher-content/') || rawUrl.startsWith('teacher-content/')) {
            if (!rawUrl.startsWith('/')) rawUrl = '/' + rawUrl;
        }

        // If it's a full URL via WordPress API proxy (which frontend might construct), let it be
        // Basic check for relative path
        imagePath = rawUrl;
    }

    const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
    const handleResetZoom = () => setScale(1);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = imagePath;
        link.download = `${lessonTitle}-infographic.${(imagePath.split('.').pop() || 'webp').split('?')[0]}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (imageError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Chưa có Infographic</h3>
                <p className="text-gray-500 max-w-md mb-6">
                    Bài học này chưa được cập nhật infographic.
                </p>
                <div className="text-xs text-gray-400 font-mono bg-gray-100 px-3 py-1 rounded break-all">
                    Path: {imagePath}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <span className="text-xl">📊</span>
                    {lessonTitle}
                </h3>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleZoomOut}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Thu nhỏ"
                    >
                        <ZoomOut className="w-5 h-5" />
                    </button>
                    <span className="text-sm font-medium text-gray-600 w-12 text-center">
                        {Math.round(scale * 100)}%
                    </span>
                    <button
                        onClick={handleZoomIn}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Phóng to"
                    >
                        <ZoomIn className="w-5 h-5" />
                    </button>
                    <div className="w-px h-6 bg-gray-200 mx-1" />
                    <button
                        onClick={handleResetZoom}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Đặt lại"
                    >
                        <RotateCcw className="w-5 h-5" />
                    </button>
                    <button
                        onClick={handleDownload}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ml-2"
                        title="Tải xuống"
                    >
                        <Download className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Image Viewer */}
            <div className="flex-1 overflow-auto bg-gray-50 p-4 flex items-center justify-center min-h-[600px]">
                <motion.div
                    animate={{ scale }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="relative shadow-lg rounded-lg overflow-hidden bg-white"
                >
                    <img
                        src={imagePath}
                        alt={`Infographic ${lessonTitle}`}
                        className="max-w-full h-auto object-contain"
                        onError={() => { if (!fromContent && ext === 'webp') setExt('png'); else setImageError(true); }}
                        style={{ maxHeight: 'none' }}
                    />
                </motion.div>
            </div>
        </div>
    );
};

export default InfographicTab;
